import {computed, effect, inject, Injectable, Injector, makeStateKey, Signal, TransferState} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {
  CancelSubscriptionCommand,
  CompleteMandatoryFlowCommand,
  UpdateAddressInfoCommand, UpdateAutoApplySettingsCommand, UpdateContactMailCommand,
  UpdateHouseholdAssetsCommand,
  UpdateHouseholdIncomeCommand,
  UpdateHouseholdMembersCommand,
  UpdateNotificationPreferencesCommand,
  UpdateRealEstateWithinNycZoneCommand,
  UpdateSignupFlowDataCommand,
  UserDto,
  HousingConnectAuthDto,
  UserPropertiesDto
} from "homey-housing-lottery-app-dto";

import {filter, first, from, map, Observable, of, ReplaySubject, startWith, switchMap, take, tap} from "rxjs";
import {ActiveUser, MandatoryFlowUser} from "../model/user";
import {AuthService} from "./auth/auth.service";
import {toSignal} from "@angular/core/rxjs-interop";
import {environment} from "../../../environments/environment";
import {StorageService} from "./storage.service";
import {AnalyticsService} from "./analytics.service";
import {ABService} from "./a-b.service";
import {isNative} from "../utils";
import {ApplicationService} from "./application.service";

export type User = UserDto | { status: 'unauthorized' };
export type SignupState = 'not-signed-up' | 'in-signup-flow' | 'previously-signed-up' | 'active' | 'previously-paid';
@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _user$ = new ReplaySubject<User>(1);
  private readonly storageService = inject(StorageService);
  private readonly injector = inject(Injector);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly abService = inject(ABService);
  private readonly transferState = inject(TransferState);

  private readonly userSignal = toSignal(this._user$);
  public readonly lastUserStatus = this.storageService.generateItemSignal(
    'LAST_USER_STATUS',
    'unauthorized'
  );
  public readonly lastUserId = this.storageService.generateItemSignal(
    'LAST_USER_ID',
    'none'
  );

  public readonly signupState: Signal<SignupState> = computed(
    () => {
      const user = this.user();
      const lastUserStatus = this.lastUserStatus();

      if (user === undefined || user.status === 'unauthorized') {
        if (lastUserStatus === 'unauthorized') {
          return 'not-signed-up';
        } else if (lastUserStatus === 'signup-flow') {
          return 'previously-signed-up';
        } else {
          return 'previously-paid'
        }
      } else {
        if (user.status === 'signup-flow') {
          return 'in-signup-flow';
        } else {
          return 'active';
        }
      }
    }
  )

  constructor() {
    const isHydrating = this.transferState.hasKey(makeStateKey<boolean>('PRERENDERED'));
    if (isHydrating) {
      this._user$.next({status: 'unauthorized'});
    }

    effect(() => {
      const user = this.userSignal();
      if (user && user.status !== 'unauthorized') {
        this.lastUserStatus.set(user.status);
        this.lastUserId.set(user.id);
      }
    })
  }

  public readonly userId = computed(() => {
    const user = this.user();
    if (user && user.status !== 'unauthorized') {
      return user.id;
    }
    return undefined;
  });

  get user(): Signal<User | undefined> {
    return this.userSignal;
  }

  public readonly userProperties = computed(() => {
    const user = this.user();

    if (!user || user.status === 'unauthorized') {
      return undefined;
    }

    return user.userProperties;
  });

  public readonly isUserPostSignupFlow = computed(
    () => {
      const user = this.user();
      return user?.status === 'active' || user?.status === 'mandatory-flow';
    }
  )

  public readonly userAfterSignupFlow = computed(
    () => {
      const user = this.user();
      return user?.status === 'signup-flow' || user?.status === 'unauthorized' ? undefined : user
    }
  )

  get user$(): Observable<User> {
    return this._user$.asObservable();
  }

  public getCurrentUserAfterSignupFlow$(): Observable<ActiveUser | MandatoryFlowUser> {
    return this.user$.pipe(
      map(user => {
        if (user.status === 'active') {
          return user;
        }

        if (user.status === 'mandatory-flow') {
          return user;
        }

        throw new Error('User is not complete');
      }),
      take(1),
    );
  }

  public retrieveUser(): Observable<void> {
    return from(this.authService.getTokens()).pipe(
      take(1),
      switchMap(tokens => {
          if (!tokens) {
            return of<User>({status: 'unauthorized'});
          }

          return this.getUser();
        }
      ),
      tap(user => {
        this._user$.next(user);
      }),
      map(() => {
      })
    )
  }

  public markSeen() {
    const user = this.user();
    const userId = (user && user.status !== 'unauthorized') ? user.id : this.lastUserId();

    if(userId !== 'none') {
      const command = {
        userId: userId,
      }
      this.http.put<void>(`${environment.apiUrl}/api/public/app/last-seen`, command).subscribe({
        error: (error) => console.error("Error marking user as seen:", error),
      });
    }
  }

  private getUser() {
    return this.http.get<User>(`${environment.apiUrl}/api/private/users`).pipe(
      switchMap(async user => {
        return user;
      })
    );
  }

  updateAddressInfo(command: UpdateAddressInfoCommand) {
    return this.applyUserUpdate(
      this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-address-info`, command)
    );
  }

  private applyUserUpdate(obs: Observable<UserDto>): Observable<void> {
    return obs.pipe(
      tap(user => this._user$.next(user)),
      map(() => {}),
    );
  }

  updateHouseholdMembers(command: UpdateHouseholdMembersCommand) {
    return this.applyUserUpdate(this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-household-members`, command));
  }

  updateIncome(command: UpdateHouseholdIncomeCommand) {
    return this.applyUserUpdate(this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-household-income`, command));
  }

  updateHouseholdAssets(command: UpdateHouseholdAssetsCommand) {
    return this.applyUserUpdate(this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-household-assets`, command));
  }

  requestDeletion() {
    return this.http.delete(`${environment.apiUrl}/api/private/users`).pipe(
    );
  }

  waitUntilProfileNoLongerInSignupFlowState(): Observable<boolean> {
    return this.waitUntilUserState(user => user.status !== 'signup-flow');
  }

  getNameByReferralCode(code: string) {
    return this.http.get(`${environment.apiUrl}/api/public/referral/name/${code}`, {responseType: 'text'});
  }

  private waitUntilUserState(predicate: (user: User) => boolean): Observable<boolean> {
    return this.user$.pipe(
      map(user => predicate(user)),
      tap(complete => {
        if (!complete) {
          setTimeout(() => this.retrieveUser().subscribe(), 1000);
        }
      }),
      filter(complete => complete),
      first(),
      startWith(false),
    );
  }

  waitUntilDeferredPaymentSettled(): Observable<boolean> {
    return this.waitUntilUserState(user => {
      if (user.status === 'mandatory-flow') {
        return user.paymentStatus === 'paid';
      }

      return user.status === 'active';
    });
  }

  waitUntilPaymentStatusPaid(): Observable<boolean> {
    return this.waitUntilUserState(user => {
      if (user.status === 'unauthorized' || user.status === 'signup-flow') {
        return false;
      }

      return user.paymentStatus === 'paid';
    });
  }

  updateSignupFlowData(updateSignupFormDataCommand: Omit<UpdateSignupFlowDataCommand, 'referralCode' | 'paymentLocationGroup'>) {
    return from(this.storageService.getItem('EXTERNAL_CODE')).pipe(
      switchMap(code => {
        const user = this.user();

        let paymentLocationGroupVariant: 'original' | 'sunk-cost' | 'sunk-cost-no-trial';
        if (user?.status === 'signup-flow' && user.email.includes('sknotrial')) {
          paymentLocationGroupVariant = 'sunk-cost-no-trial';
        } else if (user?.status === 'signup-flow' && user.email.includes('sktest')) {
          paymentLocationGroupVariant = 'sunk-cost';
        } else {
          paymentLocationGroupVariant = isNative ? this.abService.nativePaymentLocationGroup() : this.abService.paymentLocationGroup();
        }
        const paymentLocationGroup = paymentLocationGroupVariant;
        const command: UpdateSignupFlowDataCommand = {
          ...updateSignupFormDataCommand,
          paymentLocationGroup: paymentLocationGroup,
          referralCode: code?.type === 'referral' ? code.code : undefined,
        };
        return this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-signup-flow-data`, command)
      }),
      tap(user => this._user$.next(user)),
      map(() => {}));
  }

  updateRealEstateWithinNycZone(updateRealEstatateWithinNycZoneCommand: UpdateRealEstateWithinNycZoneCommand) {
    return this.applyUserUpdate(this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-real-estate-within-nyc-zone`, updateRealEstatateWithinNycZoneCommand));
  }

  completeMandatoryFlow(command: CompleteMandatoryFlowCommand) {
    this.injector.get(AnalyticsService).sendEvent(
      'mandatory-flow-success'
    );

    return this.http.put(`${environment.apiUrl}/api/private/users/complete-mandatory-flow`, command).pipe(
      switchMap(() => this.retrieveUser())
    );
  }

  updateNotificationPreferences(command: UpdateNotificationPreferencesCommand) {
    return this.applyUserUpdate(
      this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-notification-preferences`, command)
    );
  }

  updateContactMail(command: UpdateContactMailCommand) {
    return this.applyUserUpdate(
      this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-contact-mail`, command)
    );
  }

  updateAutoApplySettings(command: UpdateAutoApplySettingsCommand) {
    return this.http.put<string[]>(`${environment.apiUrl}/api/private/users/update-auto-apply-settings`, command).pipe(
      switchMap(
        (lotteries) => this.retrieveUser().pipe(
          map(() => lotteries)
        ),
      ),
      switchMap(
        lotteries => this.injector.get(ApplicationService).retrieveApplications().pipe(
          map(() => lotteries)
        )
      )
    );
  }

  refreshAutoApply() {
    return this.http.put<string[]>(`${environment.apiUrl}/api/private/users/refresh-auto-apply`, {}).pipe(
      switchMap((lotteries) => this.retrieveUser().pipe(
        map(() => lotteries)
      )),
      switchMap(
        lotteries => this.injector.get(ApplicationService).retrieveApplications().pipe(
          map(() => lotteries)
        )
      )
    );
  }

  cancelSubscription(cancelCommand: CancelSubscriptionCommand): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/api/private/users/cancel`, cancelCommand).pipe(
      switchMap(() => this.retrieveUser())
    );
  }

  updateUserProperties(properties: Partial<UserPropertiesDto>): Observable<void> {
    return this.applyUserUpdate(
      this.http.put<UserDto>(`${environment.apiUrl}/api/private/users/update-user-properties`, properties)
    );
  }

  getReferralLink(): Observable<string> {
    return this.http.get(`${environment.apiUrl}/api/private/users/referral-link`, {responseType: 'text'});
  }

  getHousingConnectAuth(): Observable<HousingConnectAuthDto> {
    return this.http.get<HousingConnectAuthDto>(`${environment.apiUrl}/api/private/users/nyc-auth`);
  }
}
